import { useState, useEffect } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash, Package, ShoppingCart, Minus, Plus } from "lucide-react";
import { MenuItem } from "@/types/menu";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { toggleMenuItemAvailability } from "@/services/menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { getTagNames } from "@/utils/tagUtils";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { useCartStore } from "@/store/cartStore";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { formatCurrency } from "@/lib/currencyFormatter";

interface MenuItemCardProps {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (id: string, name: string) => Promise<void>;
  onToggleAvailability: (id: string, available: boolean) => Promise<void>;
}

export function MenuItemCard({
  item,
  onEdit,
  onDelete,
  onToggleAvailability,
}: MenuItemCardProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isTogglingAvailability, setIsTogglingAvailability] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isMobile = useIsMobile();
  const [tagNames, setTagNames] = useState<string[]>([]);
  const { addItem, removeItem, updateQuantity, getItemQuantity } = useCartStore();

  useEffect(() => {
    // Convert tag IDs to tag names
    const fetchTagNames = async () => {
      if (item.tags && item.tags.length > 0) {
        const names = await getTagNames(item.tags);
        setTagNames(names);
      }
    };

    fetchTagNames();
  }, [item.tags]);

  const handleToggleAvailability = async () => {
    setIsTogglingAvailability(true);
    try {
      await toggleMenuItemAvailability(item.id, !item.available);
      onToggleAvailability(item.id, !item.available);
      toast({
        title: item.available
          ? t("menuItemCard.markedAsUnavailable")
          : t("menuItemCard.markedAsAvailable"),
        description: item.name,
      });
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast({
        title: t("menuItemCard.failedToToggleAvailability"),
        variant: "destructive",
      });
    } finally {
      setIsTogglingAvailability(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    await onDelete(item.id, item.name);
    setShowDeleteDialog(false);
  };

  const handleAddToCart = () => {
    if (!item.available) {
      toast({
        title: t("waiterOrdering.itemUnavailable"),
        description: t("waiterOrdering.itemUnavailableDescription"),
        variant: "destructive",
      });
      return;
    }
    
    addItem(item, 1);
    toast({
      title: t("waiterOrdering.addedToCart"),
      description: t("waiterOrdering.addedToCartDescription", { itemName: item.name }),
    });
  };

  const handleRemoveFromCart = () => {
    removeItem(item.id);
    toast({
      title: t("waiterOrdering.removedFromCart"),
      description: t("waiterOrdering.removedFromCartDescription", { itemName: item.name }),
    });
  };

  const handleAddMore = () => {
    updateQuantity(item.id, cartQuantity + 1);
    toast({
      title: t("waiterOrdering.quantityUpdated"),
      description: t("waiterOrdering.quantityUpdatedDescription", { 
        itemName: item.name, 
        quantity: cartQuantity + 1 
      }),
    });
  };

  // Format price to 2 decimal places
  const formattedPrice = formatCurrency(Number(item.price));
  
  // Get current quantity in cart
  const cartQuantity = getItemQuantity(item.id);

  // Check if inventory relationship exists
  const hasInventoryRelationship =
    item.always_available !== undefined ||
    (item.availability_dates &&
      (item.availability_dates.from || item.availability_dates.to));

  return (
    <>
      <Card
        className={`overflow-hidden ${
          !item.available ? "border-dashed opacity-70" : ""
        }`}
      >
        <div className="aspect-video relative bg-muted">
          <OptimizedImage
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
            fallbackSrc="/placeholder.svg"
            lazy={true}
          />

          {/* Inventory badges - positioned at top left */}
          {hasInventoryRelationship && (
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              <Badge variant="secondary" className="flex gap-1 items-center">
                <Package className="h-3 w-3" />
                {t("inventoryTracked")}
              </Badge>

              {/* Display stock quantity if available */}
              {typeof item.available_quantity === "number" &&
                !item.always_available && (
                  <Badge
                    variant={
                      item.available_quantity > 0 ? "outline" : "destructive"
                    }
                    className="text-xs"
                  >
                    {t("inStock")}: {item.available_quantity}
                  </Badge>
                )}

              {/* Show always available badge */}
              {item.always_available && (
                <Badge
                  variant="secondary"
                  className="text-xs bg-green-100 text-green-800 border border-green-300"
                >
                  {t("alwaysAvailable")}
                </Badge>
              )}
            </div>
          )}

          {!item.available && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
              <Badge variant="outline" className="text-sm">
                {t("menuItemCard.unavailable")}
              </Badge>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="flex flex-col space-y-1.5">
            <div className="flex justify-between items-start">
              <h3 className="font-semibold text-sm md:text-base truncate pr-2">
                {item.name}
              </h3>
              <p className="font-medium text-sm">{formattedPrice}</p>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 h-8">
              {item.description || t("menuItemCard.noDescription")}
            </p>

            <div className="flex flex-wrap gap-1 mt-1">
              {tagNames.slice(0, 3).map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-[10px]">
                  {tag}
                </Badge>
              ))}
              {tagNames.length > 3 && (
                <Badge variant="outline" className="text-[10px]">
                  +{tagNames.length - 3}
                </Badge>
              )}
            </div>

            {/* Show inventory status text */}
            {hasInventoryRelationship && (
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                {t("inventoryTracking")}
                {!item.always_available && item.availability_dates && (
                  <span className="ml-1">
                    {item.availability_dates.from && item.availability_dates.to
                      ? `(${new Date(
                          item.availability_dates.from
                        ).toLocaleDateString()} - ${new Date(
                          item.availability_dates.to
                        ).toLocaleDateString()})`
                      : item.availability_dates.from
                      ? `(From ${new Date(
                          item.availability_dates.from
                        ).toLocaleDateString()})`
                      : item.availability_dates.to
                      ? `(Until ${new Date(
                          item.availability_dates.to
                        ).toLocaleDateString()})`
                      : ""}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="p-4 pt-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <Switch
                checked={item.available}
                onCheckedChange={handleToggleAvailability}
                disabled={isTogglingAvailability}
                className="data-[state=checked]:bg-primary"
              />
              <span className="text-xs text-muted-foreground">
                {item.available
                  ? t("menuItemCard.available")
                  : t("menuItemCard.unavailable")}
              </span>
            </div>

            <div className="flex gap-1">
              {cartQuantity > 0 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveFromCart}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    title={t("waiterOrdering.removeFromCart")}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Badge 
                    variant="secondary" 
                    className="h-6 px-2 text-xs"
                  >
                    {cartQuantity}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddMore}
                    className="h-8 w-8 p-0"
                    title={t("waiterOrdering.addMore")}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddToCart}
                  disabled={!item.available}
                  className="h-8 w-8 p-0"
                  title={t("waiterOrdering.addToCart")}
                >
                  <ShoppingCart className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(item)}
                className="h-8 w-8 p-0"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteClick}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("menuItemCard.deleteConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("menuItemCard.deleteConfirmDescription", {
                name: item.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("menuItemCard.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("menuItemCard.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
